import React, { useState, useEffect } from 'react';
import {
    ChevronUpIcon,
    ChevronDownIcon,
    ExclamationTriangleIcon,
    XCircleIcon,
} from '@heroicons/react/20/solid';
import {
    ValidationError,
    ValidationResult,
} from '../validation/schemaValidator';

interface DebugPaneProps {
    validationResult: ValidationResult | null;
    isLoading?: boolean;
}

export default function DebugPane({
    validationResult,
    isLoading = false,
}: DebugPaneProps) {
    const [isExpanded, setIsExpanded] = useState(false);
    const [selectedTab, setSelectedTab] = useState<'errors' | 'warnings'>(
        'errors',
    );

    const errorCount = validationResult?.errors.length || 0;
    const warningCount = validationResult?.warnings.length || 0;
    const totalIssues = errorCount + warningCount;

    // Auto-select appropriate tab when there are errors, but don't auto-expand
    useEffect(() => {
        if (errorCount > 0) {
            setSelectedTab('errors');
        } else if (warningCount > 0 && errorCount === 0) {
            setSelectedTab('warnings');
        }
    }, [errorCount, warningCount]);

    const getStatusColor = () => {
        if (isLoading) return 'bg-blue-500';
        if (errorCount > 0) return 'bg-red-500';
        if (warningCount > 0) return 'bg-yellow-500';
        return 'bg-green-500';
    };

    const getStatusText = () => {
        if (isLoading) return 'Validating...';
        if (errorCount > 0)
            return `${errorCount} error${errorCount !== 1 ? 's' : ''}`;
        if (warningCount > 0)
            return `${warningCount} warning${warningCount !== 1 ? 's' : ''}`;
        return 'No issues';
    };

    const renderIssueItem = (issue: ValidationError, index: number) => (
        <div key={index} className="border-l-4 border-l-gray-200 pl-4 py-2">
            <div className="flex items-start space-x-2">
                {issue.severity === 'error' ? (
                    <XCircleIcon className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                ) : (
                    <ExclamationTriangleIcon className="w-5 h-5 text-yellow-500 mt-0.5 flex-shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2">
                        <span className="text-xs font-mono text-gray-600 bg-gray-100 px-2 py-1 rounded">
                            {issue.path}
                        </span>
                    </div>
                    <p className="text-sm text-gray-900 mt-1">
                        {issue.message}
                    </p>
                    {issue.suggestion && (
                        <p className="text-sm text-gray-600 mt-1 italic">
                            💡 {issue.suggestion}
                        </p>
                    )}
                </div>
            </div>
        </div>
    );

    return (
        <div className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200 shadow-lg">
            {/* Status Bar */}
            <div
                className="flex items-center justify-between px-4 py-2 cursor-pointer hover:bg-gray-50"
                onClick={() => setIsExpanded(!isExpanded)}
            >
                <div className="flex items-center space-x-3">
                    <div
                        className={`w-3 h-3 rounded-full ${getStatusColor()}`}
                    />
                    <span className="text-sm font-medium text-gray-900">
                        Schema Validation
                    </span>
                    <span className="text-sm text-gray-600">
                        {getStatusText()}
                    </span>
                    {totalIssues > 0 && (
                        <div className="flex items-center space-x-2">
                            {errorCount > 0 && (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                    {errorCount} error
                                    {errorCount !== 1 ? 's' : ''}
                                </span>
                            )}
                            {warningCount > 0 && (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                    {warningCount} warning
                                    {warningCount !== 1 ? 's' : ''}
                                </span>
                            )}
                        </div>
                    )}
                </div>
                <div className="flex items-center space-x-2">
                    {isLoading && (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500" />
                    )}
                    {isExpanded ? (
                        <ChevronDownIcon className="w-5 h-5 text-gray-400" />
                    ) : (
                        <ChevronUpIcon className="w-5 h-5 text-gray-400" />
                    )}
                </div>
            </div>

            {/* Expanded Content */}
            {isExpanded && (
                <div className="border-t border-gray-200 bg-gray-50">
                    {totalIssues > 0 ? (
                        <>
                            {/* Tabs */}
                            <div className="flex border-b border-gray-200 bg-white">
                                {errorCount > 0 && (
                                    <button
                                        onClick={() => setSelectedTab('errors')}
                                        className={`px-4 py-2 text-sm font-medium border-b-2 ${
                                            selectedTab === 'errors'
                                                ? 'border-red-500 text-red-600 bg-red-50'
                                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                        }`}
                                    >
                                        Errors ({errorCount})
                                    </button>
                                )}
                                {warningCount > 0 && (
                                    <button
                                        onClick={() =>
                                            setSelectedTab('warnings')
                                        }
                                        className={`px-4 py-2 text-sm font-medium border-b-2 ${
                                            selectedTab === 'warnings'
                                                ? 'border-yellow-500 text-yellow-600 bg-yellow-50'
                                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                        }`}
                                    >
                                        Warnings ({warningCount})
                                    </button>
                                )}
                            </div>

                            {/* Content */}
                            <div className="max-h-80 overflow-y-auto">
                                <div className="p-4 space-y-3">
                                    {selectedTab === 'errors' &&
                                        errorCount > 0 && (
                                            <>
                                                {validationResult?.errors.map(
                                                    (error, index) =>
                                                        renderIssueItem(
                                                            error,
                                                            index,
                                                        ),
                                                )}
                                            </>
                                        )}
                                    {selectedTab === 'warnings' &&
                                        warningCount > 0 && (
                                            <>
                                                {validationResult?.warnings.map(
                                                    (warning, index) =>
                                                        renderIssueItem(
                                                            warning,
                                                            index,
                                                        ),
                                                )}
                                            </>
                                        )}
                                </div>
                            </div>
                        </>
                    ) : (
                        <div className="p-4 text-center">
                            <div className="flex items-center justify-center space-x-2 text-green-600">
                                <div className="w-5 h-5 rounded-full bg-green-500" />
                                <span className="text-sm font-medium">
                                    {isLoading
                                        ? 'Validating schema...'
                                        : 'Schema validation passed! No issues found.'}
                                </span>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
